import {
  ConnectionQuality,
  ConnectionState,
  LocalAudioTrack,
  LocalParticipant,
  LogLevel,
  MediaDeviceFailure,
  ParticipantEvent,
  RemoteParticipant,
  RemoteVideoTrack,
  Room,
  RoomEvent,
  Track,
  VideoPresets,
  VideoPresets43,
  createAudioAnalyser,
  facingModeFromLocalTrack,
  setLogLevel,
} from "livekit-client";
import { BackgroundBlur, VirtualBackground } from "@livekit/track-processors";

const $ = (id) => document.getElementById(id);

const state = {
  defaultDevices: new Map(),
  bitrateInterval: undefined,
  blur: BackgroundBlur(10, { delegate: "GPU" }),
  virtualBackground: VirtualBackground(
    "/images/samantha-gades-BlIhVfXbi9s-unsplash.jpg"
  ),
};
let currentRoom;

let startTime;

// const searchParams = new URLSearchParams(window.location.search);
// const storedUrl = searchParams.get("url") ?? "ws://localhost:7880";
// const storedToken = searchParams.get("token") ?? "";
// $("url").value = storedUrl;
// $("token").value = storedToken;

function updateSearchParams(url, token) {
  const params = new URLSearchParams({ url, token });
  window.history.replaceState(
    null,
    "",
    `${window.location.pathname}?${params.toString()}`
  );
}

const appActions = {
  connectWithFormInput: async () => {
    const url = $("url").value;
    const token = $("token").value;

    setLogLevel(LogLevel.debug);
    updateSearchParams(url, token);

    const roomOpts = {
      adaptiveStream: true,
      dynacast: true,
      publishDefaults: {
        simulcast: true,
        videoSimulcastLayers: [VideoPresets43.h120, VideoPresets43.h240],
      },
      videoCaptureDefaults: {
        resolution: VideoPresets43.h540.resolution,
      },
    };

    const connectOpts = {
      autoSubscribe: true,
    };
    await appActions.connectToRoom(url, token, roomOpts, connectOpts, true);

    state.bitrateInterval = setInterval(renderBitrate, 1000);
  },

  connectToRoom: async (
    url,
    token,
    roomOptions,
    connectOptions,
    shouldPublish
  ) => {
    const room = new Room(roomOptions);

    startTime = Date.now();
    await room.prepareConnection(url);
    const prewarmTime = Date.now() - startTime;
    appendLog(`prewarmed connection in ${prewarmTime}ms`);

    room
      .on(RoomEvent.ParticipantConnected, participantConnected)
      .on(RoomEvent.ParticipantDisconnected, participantDisconnected)
      .on(RoomEvent.Disconnected, handleRoomDisconnect)
      .on(RoomEvent.Reconnecting, () => appendLog("Reconnecting to room"))
      .on(RoomEvent.Reconnected, async () => {
        appendLog(
          "Successfully reconnected. server",
          await room.engine.getConnectedServerAddress()
        );
      })
      .on(RoomEvent.LocalTrackPublished, (pub) => {
        const track = pub.track;

        if (track instanceof LocalAudioTrack) {
          const { calculateVolume } = createAudioAnalyser(track);

          setInterval(() => {
            $("local-volume")?.setAttribute(
              "value",
              calculateVolume().toFixed(4)
            );
          }, 200);
        }
        renderParticipant(room.localParticipant);
        updateButtonsForPublishState();
      })
      .on(RoomEvent.LocalTrackUnpublished, () => {
        renderParticipant(room.localParticipant);
        updateButtonsForPublishState();
      })
      .on(RoomEvent.RoomMetadataChanged, (metadata) => {
        appendLog("new metadata for room", metadata);
      })
      .on(RoomEvent.MediaDevicesChanged, handleDevicesChanged)
      .on(RoomEvent.AudioPlaybackStatusChanged, () => {
        if (room.canPlaybackAudio) {
          $("start-audio-button")?.setAttribute("disabled", "true");
        } else {
          $("start-audio-button")?.removeAttribute("disabled");
        }
      })
      .on(RoomEvent.MediaDevicesError, (e) => {
        const failure = MediaDeviceFailure.getFailure(e);
        appendLog("media device failure", failure);
      })
      .on(RoomEvent.ConnectionQualityChanged, (quality, participant) => {
        appendLog("connection quality changed", participant?.identity, quality);
      })
      .on(RoomEvent.TrackSubscribed, (track, pub, participant) => {
        appendLog("subscribed to track", pub.trackSid, participant.identity);
        renderParticipant(participant);
      })
      .on(RoomEvent.TrackUnsubscribed, (_, pub, participant) => {
        appendLog("unsubscribed from track", pub.trackSid);
        renderParticipant(participant);
      })
      .on(RoomEvent.SignalConnected, async () => {
        const signalConnectionTime = Date.now() - startTime;
        appendLog(`signal connection established in ${signalConnectionTime}ms`);
        if (shouldPublish) {
          await room.localParticipant.enableCameraAndMicrophone();
          appendLog(`tracks published in ${Date.now() - startTime}ms`);
          updateButtonsForPublishState();
        }
      });

    try {
      await room.connect(url, token, connectOptions);
      const elapsed = Date.now() - startTime;
      appendLog(
        `successfully connected to ${room.name} in ${Math.round(elapsed)}ms`,
        await room.engine.getConnectedServerAddress()
      );
    } catch (error) {
      let message = error;
      if (error.message) {
        message = error.message;
      }
      appendLog("could not connect:", message);
      return;
    }
    currentRoom = room;
    window.currentRoom = room;
    setButtonsForState(true);

    room.remoteParticipants.forEach((participant) => {
      participantConnected(participant);
    });
    participantConnected(room.localParticipant);

    return room;
  },

  toggleAudio: async () => {
    if (!currentRoom) return;
    const enabled = currentRoom.localParticipant.isMicrophoneEnabled;
    setButtonDisabled("toggle-audio-button", true);
    if (enabled) {
      appendLog("disabling audio");
    } else {
      appendLog("enabling audio");
    }
    await currentRoom.localParticipant.setMicrophoneEnabled(!enabled);
    setButtonDisabled("toggle-audio-button", false);
    updateButtonsForPublishState();
  },

  toggleVideo: async () => {
    if (!currentRoom) return;
    setButtonDisabled("toggle-video-button", true);
    const enabled = currentRoom.localParticipant.isCameraEnabled;
    if (enabled) {
      appendLog("disabling video");
    } else {
      appendLog("enabling video");
    }
    await currentRoom.localParticipant.setCameraEnabled(!enabled);

    setButtonDisabled("toggle-video-button", false);
    renderParticipant(currentRoom.localParticipant);

    updateButtonsForPublishState();
  },

  flipVideo: () => {
    const videoPub = currentRoom?.localParticipant.getTrackPublication(
      Track.Source.Camera
    );
    if (!videoPub) {
      return;
    }
    const track = videoPub.track;
    const facingMode = track && facingModeFromLocalTrack(track);
    if (facingMode?.facingMode === "environment") {
      setButtonState("flip-video-button", "Front Camera", false);
    } else {
      setButtonState("flip-video-button", "Back Camera", false);
    }
    const options = {
      resolution: VideoPresets.h720.resolution,
      facingMode:
        facingMode?.facingMode === "environment" ? "user" : "environment",
    };
    videoPub.videoTrack?.restartTrack(options);
  },

  toggleBlur: async () => {
    if (!currentRoom) return;
    setButtonDisabled("toggle-blur-button", true);

    try {
      const camTrack = currentRoom.localParticipant.getTrackPublication(
        Track.Source.Camera
      ).track;
      if (camTrack.getProcessor()?.name !== "background-blur") {
        await camTrack.stopProcessor();
        await camTrack.setProcessor(state.blur);
      } else {
        await camTrack.stopProcessor();
      }
    } catch (e) {
      appendLog(`ERROR: ${e.message}`);
    } finally {
      setButtonDisabled("toggle-blur-button", false);
      renderParticipant(currentRoom.localParticipant);
      updateButtonsForPublishState();
    }
  },

  toggleVirtualBackground: async () => {
    if (!currentRoom) return;
    setButtonDisabled("toggle-blur-button", true);
    try {
      const camTrack = currentRoom.localParticipant.getTrackPublication(
        Track.Source.Camera
      ).track;
      if (camTrack.getProcessor()?.name !== "virtual-background") {
        await camTrack.stopProcessor();
        await camTrack.setProcessor(state.virtualBackground);
      } else {
        await camTrack.stopProcessor();
      }
    } catch (e) {
      appendLog(`ERROR: ${e.message}`);
    } finally {
      setButtonDisabled("toggle-blur-button", false);
      renderParticipant(currentRoom.localParticipant);
      updateButtonsForPublishState();
    }
  },

  updateVirtualBackgroundImage: async (imagePath) => {
    if (!currentRoom) return;

    try {
      const camTrack = currentRoom.localParticipant.getTrackPublication(
        Track.Source.Camera
      ).track;
      await state.virtualBackground.updateTransformerOptions({ imagePath });
      if (camTrack.getProcessor()?.name !== "virtual-background") {
        await camTrack.stopProcessor();
        await camTrack.setProcessor(state.virtualBackground);
      }
    } catch (e) {
      appendLog(`ERROR: ${e.message}`);
    } finally {
      setButtonDisabled("toggle-blur-button", false);
      renderParticipant(currentRoom.localParticipant);
      updateButtonsForPublishState();
    }
  },

  startAudio: () => {
    currentRoom?.startAudio();
  },

  disconnectRoom: () => {
    if (currentRoom) {
      currentRoom.disconnect();
    }
    if (state.bitrateInterval) {
      clearInterval(state.bitrateInterval);
    }
  },

  handleDeviceSelected: async (e) => {
    const deviceId = e.target.value;
    const elementId = e.target.id;
    const kind = elementMapping[elementId];
    if (!kind) {
      return;
    }

    state.defaultDevices.set(kind, deviceId);

    if (currentRoom) {
      await currentRoom.switchActiveDevice(kind, deviceId);
    }
  },
};

window.appActions = appActions;
export default appActions;

function participantConnected(participant) {
  appendLog(
    "participant",
    participant.identity,
    "connected",
    participant.metadata
  );
  participant
    .on(ParticipantEvent.TrackMuted, (pub) => {
      appendLog("track was muted", pub.trackSid, participant.identity);
      renderParticipant(participant);
    })
    .on(ParticipantEvent.TrackUnmuted, (pub) => {
      appendLog("track was unmuted", pub.trackSid, participant.identity);
      renderParticipant(participant);
    })
    .on(ParticipantEvent.IsSpeakingChanged, () => {
      renderParticipant(participant);
    })
    .on(ParticipantEvent.ConnectionQualityChanged, () => {
      renderParticipant(participant);
    });
}

function participantDisconnected(participant) {
  appendLog("participant", participant.sid, "disconnected");

  renderParticipant(participant, true);
}

function handleRoomDisconnect(reason) {
  if (!currentRoom) return;
  appendLog("disconnected from room", { reason });
  setButtonsForState(false);
  renderParticipant(currentRoom.localParticipant, true);
  currentRoom.remoteParticipants.forEach((p) => {
    renderParticipant(p, true);
  });

  const container = $("participants-area");
  if (container) {
    container.innerHTML = "";
  }

  currentRoom = undefined;
  window.currentRoom = undefined;
}

function appendLog(...args) {
  const logger = $("log");
  for (let i = 0; i < arguments.length; i += 1) {
    if (typeof args[i] === "object") {
      logger.innerHTML += `${
        JSON && JSON.stringify ? JSON.stringify(args[i], undefined, 2) : args[i]
      } `;
    } else {
      logger.innerHTML += `${args[i]} `;
    }
  }
  logger.innerHTML += "\n";
  (() => {
    logger.scrollTop = logger.scrollHeight;
  })();
}

function renderParticipant(participant, remove = false) {
  const container = $("participants-area");
  if (!container) return;
  const { identity } = participant;
  let div = $(`participant-${identity}`);
  if (!div && !remove) {
    div = document.createElement("div");
    div.id = `participant-${identity}`;
    div.className = "participant";
    div.innerHTML = `
      <video id="video-${identity}"></video>
      <audio id="audio-${identity}"></audio>
      <div class="info-bar">
        <div id="name-${identity}" class="name">
        </div>
        <div style="text-align: center;">
          <span id="codec-${identity}" class="codec">
          </span>
          <span id="size-${identity}" class="size">
          </span>
          <span id="bitrate-${identity}" class="bitrate">
          </span>
        </div>
        <div class="right">
          <span id="signal-${identity}"></span>
          <span id="mic-${identity}" class="mic-on"></span>
        </div>
      </div>
      ${
        participant instanceof RemoteParticipant
          ? `<div class="volume-control">
        <input id="volume-${identity}" type="range" min="0" max="1" step="0.1" value="1" orient="vertical" />
      </div>`
          : `<progress id="local-volume" max="1" value="0" />`
      }

    `;
    container.appendChild(div);

    const sizeElm = $(`size-${identity}`);
    const videoElm = $(`video-${identity}`);
    videoElm.onresize = () => {
      updateVideoSize(videoElm, sizeElm);
    };
  }
  const videoElm = $(`video-${identity}`);
  const audioELm = $(`audio-${identity}`);
  if (remove) {
    div?.remove();
    if (videoElm) {
      videoElm.srcObject = null;
      videoElm.src = "";
    }
    if (audioELm) {
      audioELm.srcObject = null;
      audioELm.src = "";
    }
    return;
  }

  $(`name-${identity}`).innerHTML = participant.identity;
  if (participant instanceof LocalParticipant) {
    $(`name-${identity}`).innerHTML += " (you)";
  }
  const micElm = $(`mic-${identity}`);
  const signalElm = $(`signal-${identity}`);
  const cameraPub = participant.getTrackPublication(Track.Source.Camera);
  const micPub = participant.getTrackPublication(Track.Source.Microphone);
  if (participant.isSpeaking) {
    div.classList.add("speaking");
  } else {
    div.classList.remove("speaking");
  }

  if (participant instanceof RemoteParticipant) {
    const volumeSlider = $(`volume-${identity}`);
    volumeSlider.addEventListener("input", (ev) => {
      participant.setVolume(Number.parseFloat(ev.target.value));
    });
  }

  const cameraEnabled =
    cameraPub && cameraPub.isSubscribed && !cameraPub.isMuted;
  if (cameraEnabled) {
    if (participant instanceof LocalParticipant) {
      videoElm.style.transform = "scale(-1, 1)";
    } else if (!cameraPub?.videoTrack?.attachedElements.includes(videoElm)) {
      const renderStartTime = Date.now();
      videoElm.onloadeddata = () => {
        const elapsed = Date.now() - renderStartTime;
        let fromJoin = 0;
        if (
          participant.joinedAt &&
          participant.joinedAt.getTime() < startTime
        ) {
          fromJoin = Date.now() - startTime;
        }
        appendLog(
          `RemoteVideoTrack ${cameraPub?.trackSid} (${videoElm.videoWidth}x${videoElm.videoHeight}) rendered in ${elapsed}ms`,
          fromJoin > 0 ? `, ${fromJoin}ms from start` : ""
        );
      };
    }
    cameraPub?.videoTrack?.attach(videoElm);
  } else {
    $(`size-${identity}`).innerHTML = "";
    if (cameraPub?.videoTrack) {
      cameraPub.videoTrack?.detach(videoElm);
    } else {
      videoElm.src = "";
      videoElm.srcObject = null;
    }
  }

  const micEnabled = micPub && micPub.isSubscribed && !micPub.isMuted;
  if (micEnabled) {
    if (!(participant instanceof LocalParticipant)) {
      audioELm.onloadeddata = () => {
        if (
          participant.joinedAt &&
          participant.joinedAt.getTime() < startTime
        ) {
          const fromJoin = Date.now() - startTime;
          appendLog(
            `RemoteAudioTrack ${micPub?.trackSid} played ${fromJoin}ms from start`
          );
        }
      };
      micPub?.audioTrack?.attach(audioELm);
    }
    micElm.className = "mic-on";
    micElm.innerHTML = '<i class="fas fa-microphone"></i>';
  } else {
    micElm.className = "mic-off";
    micElm.innerHTML = '<i class="fas fa-microphone-slash"></i>';
  }

  switch (participant.connectionQuality) {
    case ConnectionQuality.Excellent:
    case ConnectionQuality.Good:
    case ConnectionQuality.Poor:
      signalElm.className = `connection-${participant.connectionQuality}`;
      signalElm.innerHTML = '<i class="fas fa-circle"></i>';
      break;
    default:
      signalElm.innerHTML = "";
  }
}

function renderBitrate() {
  if (!currentRoom || currentRoom.state !== ConnectionState.Connected) {
    return;
  }
  const participants = [...currentRoom.remoteParticipants.values()];
  participants.push(currentRoom.localParticipant);

  for (const p of participants) {
    const elm = $(`bitrate-${p.identity}`);
    let totalBitrate = 0;
    for (const t of p.trackPublications.values()) {
      if (t.track) {
        totalBitrate += t.track.currentBitrate;
      }

      if (t.source === Track.Source.Camera) {
        if (t.videoTrack instanceof RemoteVideoTrack) {
          const codecElm = $(`codec-${p.identity}`);
          codecElm.innerHTML = t.videoTrack.getDecoderImplementation() ?? "";
        }
      }
    }
    let displayText = "";
    if (totalBitrate > 0) {
      displayText = `${Math.round(totalBitrate / 1024).toLocaleString()} kbps`;
    }
    if (elm) {
      elm.innerHTML = displayText;
    }
  }
}

function updateVideoSize(element, target) {
  target.innerHTML = `(${element.videoWidth}x${element.videoHeight})`;
}

function setButtonState(
  buttonId,
  buttonText,
  isActive,
  isDisabled = undefined
) {
  const el = $(buttonId);
  if (!el) return;
  if (isDisabled !== undefined) {
    el.disabled = isDisabled;
  }
  el.innerHTML = buttonText;
  if (isActive) {
    el.classList.add("active");
  } else {
    el.classList.remove("active");
  }
}

function setButtonDisabled(buttonId, isDisabled) {
  const el = $(buttonId);
  el.disabled = isDisabled;
}

setTimeout(handleDevicesChanged, 100);

function setButtonsForState(connected) {
  const connectedSet = [
    "toggle-video-button",
    "toggle-audio-button",
    "disconnect-room-button",
    "toggle-blur-button",
    "toggle-virtual-bg-button",
  ];
  const disconnectedSet = ["connect-button"];

  const toRemove = connected ? connectedSet : disconnectedSet;
  const toAdd = connected ? disconnectedSet : connectedSet;

  toRemove.forEach((id) => $(id)?.removeAttribute("disabled"));
  toAdd.forEach((id) => $(id)?.setAttribute("disabled", "true"));
}

const elementMapping = {
  "video-input": "videoinput",
  "audio-input": "audioinput",
  "audio-output": "audiooutput",
};

async function handleDevicesChanged() {
  Promise.all(
    Object.keys(elementMapping).map(async (id) => {
      const kind = elementMapping[id];
      if (!kind) {
        return;
      }
     // const devices = await Room.getLocalDevices(kind);
     // const element = $(id);
   //   populateSelect(element, devices, state.defaultDevices.get(kind));
    })
  );
}



function updateButtonsForPublishState() {
  if (!currentRoom) {
    return;
  }
  const lp = currentRoom.localParticipant;
  setButtonState(
    "toggle-video-button",
    `${lp.isCameraEnabled ? "Disable" : "Enable"} Video`,
    lp.isCameraEnabled
  );

  setButtonState(
    "toggle-audio-button",
    `${lp.isMicrophoneEnabled ? "Disable" : "Enable"} Audio`,
    lp.isMicrophoneEnabled
  );
}

async function acquireDeviceList() {
  handleDevicesChanged();
}

acquireDeviceList();
